import RouteTemplate from 'ember-route-template'
import { ReportingTeacherUiHeader } from 'teacher-ui-client/components/reporting/teacher-ui-header'
import { ReportingDetailedControls } from 'teacher-ui-client/components/reporting/detailed-controls'
import { ReportingNoDataMessageIf } from 'teacher-ui-client/components/reporting/no-data-message-if'
import { TilesReporting } from 'teacher-ui-client/components/tiles/reporting'
import { t } from 'ember-intl'
import { array, fn } from '@ember/helper'
import { ExportType } from 'teacher-ui-client/components/reporting/export-select'
import type ReStudentSummaryDrivingTestsRoute from 'teacher-ui-client/reporting/ui-scope/re/student-summary/driving-tests/route'
import { not, eq } from 'ember-truth-helpers'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import Tooltip from 'teacher-ui-client/components/tooltip'
import { DrivingTestsTotalsByMonth } from 'teacher-ui-client/components/giraffe/charts/driving-tests/totals-by-month'
import DataTableOperations from '@blakeelearning/data-tables/components/data-table-operations'
import { on } from '@ember/modifier'
import { BasePageSelector } from 'teacher-ui-client/components/base/page-selector'
import Component from '@glimmer/component'
import { service } from '@ember/service'
import { isEmpty } from '@ember/utils'
import { format } from 'date-fns'
import { dtoComputed } from '@blakeelearning/data-tables/components/data-table-operations'
import { tracked } from '@glimmer/tracking'
import { statefulSort, type TableAction } from 'teacher-ui-client/utils/giraffe/stateful-sort'
import type { IntlService } from 'ember-intl'
import { mapValues, truncate } from 'lodash'
import type { PhosphorIconName } from 'phosphor-icons'

type DTOComponentContext = any

export interface DrivingTestDetailsHistory {
  category: string
  created_at: number
  passed: boolean
  quiz_position: number
  correct_answers: string[]
  incorrect_answers: Record<string, string>
}

interface DrivingTestsTableDataItem {
  area: string
  correctAnswers: string
  incorrectAnswers: Record<string, string>
  correct: number
  passed: string
  date: Date
  dateFormatted: string
}

interface Signature {
  Args: {
    model: ModelFor<ReStudentSummaryDrivingTestsRoute>
  }
}

export class ReStudentSummaryDrivingTestsPage extends Component<Signature> {
  @service intl!: IntlService

  @tracked currentSortIcon: PhosphorIconName = 'phosphor-caret-up-down-fill'
  @tracked currentSortColumn?: string

  @tracked currentPage = 1
  @tracked perPage = 7

  get drivingTestsTableData(): DrivingTestsTableDataItem[] {
    const { intl } = this
    const dateFormatString = intl.t('dateFormats.dateFns.dd MMM')
    const data = this.args.model.drivingTestDetailsHistory
    if (!data) return []

    return data.map(
      (
        {
          category,
          quiz_position: quizPosition,
          correct_answers: correctOrNull,
          incorrect_answers: incorrectOrNull,
          passed: isPassed,
          created_at,
        },
        index,
      ) => {
        const rowId = index.toString()
        const categoryName = intl.t(`driving_tests.re.categories.${category}`)
        const area = `${categoryName} ${quizPosition}`
        const correctArray = correctOrNull ?? []
        const correct = correctArray.length
        const correctAnswers = correctArray.join(', ')
        const incorrectAnswers = mapValues(incorrectOrNull, (ans) => truncate(ans, { length: 20 }))
        const date = new Date(created_at * 1000)
        const dateFormatted = format(date, dateFormatString)
        const passed = isPassed ? 'Yes' : 'No'
        return { rowId, area, correctAnswers, incorrectAnswers, correct, passed, date, dateFormatted }
      },
    )
  }

  get dataOperations() {
    return {
      doPagination: dtoComputed(
        'data.[]',
        'currentPage',
        'perPage',
        function (this: DTOComponentContext, data: DrivingTestsTableDataItem[]) {
          const { currentPage, perPage } = this
          if (isEmpty(currentPage) || isEmpty(perPage)) return data
          const upperIndex = currentPage * perPage
          const lowerIndex = (currentPage - 1) * perPage
          return data.slice(lowerIndex, upperIndex)
        },
      ),
    }
  }

  get pageCount() {
    const dataLength = this.args.model.drivingTestDetailsHistory?.length ?? 0
    const { perPage } = this
    return Math.ceil(dataLength / perPage)
  }

  operationsStack(this: DTOComponentContext) {
    return [...this.defaultOperationsStack, 'doPagination']
  }

  changePage = (page: number) => {
    this.currentPage = page
  }

  changePerPage = (perPage: number) => {
    this.perPage = perPage
    this.currentPage = 1 // reset to first page when perPage changes
  }

  statefulSort = (tableAction: TableAction, tableActionOperation: string, tableActionKey: string) => {
    return statefulSort(this, tableAction, tableActionOperation, tableActionKey)
  }

  <template>
    <ReportingTeacherUiHeader
      @title={{t "reporting.title.studentDetails.drivingTestDetails" student=@model.student.fullName}}
      @showBack={{true}}
    />

    <ReportingDetailedControls
      @students={{@model.students}}
      @exportOptions={{array ExportType.print}}
      @isDataPresent={{Boolean @model.drivingTestDetailsTotalsByMonth}}
      @showDatePicker={{true}}
      @studentRouteName="reporting.ui-scope.re.student-summary.driving-tests"
    />

    <ReportingNoDataMessageIf @on={{not @model.drivingTestDetailsTotalsByMonth}}>
      {{#if @model.drivingTestDetailsTotalsByMonth}}
        <TilesReporting class="relative mb-5">
          <div>
            <DrivingTestsTotalsByMonth
              @data={{@model.drivingTestDetailsTotalsByMonth}}
              @title={{t "reporting.chartTitles.drivingTestDetailsTotalsByMonth"}}
            />
          </div>
          <span class="absolute top-4 right-4 leading-none print:hidden">
            <PhosphorIcon @icon="phosphor-info-fill" class="text-dusty-black-500 h-3 w-3 cursor-pointer" />
            <Tooltip @text={{t "reporting.tooltips.drivingTestDetailsTotalsByMonth"}} />
          </span>
        </TilesReporting>
      {{/if}}

      {{#if @model.drivingTestDetailsHistory}}
        <DataTableOperations
          @dataOperations={{this.dataOperations}}
          @operationsStack={{this.operationsStack}}
          @currentPage={{this.currentPage}}
          @perPage={{this.perPage}}
          @data={{this.drivingTestsTableData}}
          as |transformedData tableAction|
        >
          <div
            class="border-dusty-black-50 shadow-dusty-black-100 overflow-hidden rounded-md border bg-white shadow-md"
          >
            <h3 class="border-oceany-blue-150 border-b-4 p-4 print:border-black">
              {{t "reporting.re.drivingTestsStudent.table.title"}}
            </h3>
            <table data-test-driving-tests-student-details-table class="w-full rounded-xs [&_svg]:ml-4">
              <thead>
                <tr class="bg-oceany-blue-100">
                  <th class="border-r border-b border-white p-4">
                    <button
                      type="button"
                      class="flex w-full items-center justify-between"
                      {{on "click" (fn this.statefulSort tableAction "sortByValue" "area")}}
                    >
                      {{t "reporting.re.drivingTestsStudent.table.headers.area"}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "area")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0"
                      />
                    </button>
                  </th>
                  <th class="border-r border-b border-white p-4">
                    <button
                      type="button"
                      class="flex w-full items-center justify-between"
                      {{on "click" (fn this.statefulSort tableAction "sortByDate" "date")}}
                    >
                      {{t "reporting.re.drivingTestsStudent.table.headers.date"}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "date")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0"
                      />
                    </button>
                  </th>
                  <th class="border-r border-b border-white p-4">
                    <button
                      type="button"
                      class="flex w-full items-center justify-between"
                      {{on "click" (fn this.statefulSort tableAction "sortByNumber" "correct")}}
                    >
                      {{t "reporting.re.drivingTestsStudent.table.headers.correct"}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "correct")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0"
                      />
                    </button>
                  </th>
                  <th class="border-r border-b border-white p-4">
                    <button
                      type="button"
                      class="flex w-full items-center justify-between"
                      {{on "click" (fn this.statefulSort tableAction "sortByValue" "correctAnswers")}}
                    >
                      {{t "reporting.re.drivingTestsStudent.table.headers.correctAnswers"}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "correctAnswers")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0"
                      />
                    </button>
                  </th>
                  <th class="border-r border-b border-white p-4">
                    <button
                      type="button"
                      class="flex w-full items-center justify-between"
                      {{on "click" (fn this.statefulSort tableAction "sortByValue" "incorrectAnswers")}}
                    >
                      {{t "reporting.re.drivingTestsStudent.table.headers.inCorrectAnswers"}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "incorrectAnswers")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0"
                      />
                    </button>
                  </th>
                  <th class="border-r border-b border-white p-4">
                    <button
                      type="button"
                      class="flex w-full items-center justify-between"
                      {{on "click" (fn this.statefulSort tableAction "sortByValue" "passed")}}
                    >
                      {{t "reporting.re.drivingTestsStudent.table.headers.passed"}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "passed")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0"
                      />
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {{#each transformedData as |row index|}}
                  <tr
                    data-test-driving-test-student-row={{index}}
                    class="even:bg-oceany-blue-50 hover:bg-oceany-blue-100 cursor-pointer bg-white"
                  >
                    <td class="w-1/12 p-3 text-center whitespace-nowrap">
                      {{row.area}}
                    </td>
                    <td class="w-1/12 p-4 text-center">
                      {{row.dateFormatted}}
                    </td>
                    <td class="w-1/12 p-4 text-center">
                      {{row.correct}}
                    </td>
                    <td class="w-5/12 px-5 py-3 text-left">
                      {{row.correctAnswers}}
                    </td>
                    <td class="w-3/12 px-5 py-3 text-center">
                      {{#each-in row.incorrectAnswers as |correctWord incorrectSpelling|}}
                        <span
                          after=", "
                          class="whitespace-nowrap after:relative after:content-[attr(after)] last:after:hidden"
                        >{{String correctWord}} ({{incorrectSpelling}})</span>
                      {{else}}
                        -
                      {{/each-in}}
                    </td>
                    <td class="passed w-1/12 p-4 text-center">
                      {{row.passed}}
                    </td>
                  </tr>
                {{/each}}
              </tbody>
            </table>
          </div>
        </DataTableOperations>
        <BasePageSelector
          class="my-4"
          @pageCount={{this.pageCount}}
          @currentPage={{this.currentPage}}
          @changePage={{this.changePage}}
          @itemCount={{@model.drivingTestDetailsHistory.length}}
          @itemsPerPage={{this.perPage}}
          @changePerPage={{this.changePerPage}}
        />
      {{/if}}
    </ReportingNoDataMessageIf>
  </template>
}

export default RouteTemplate(ReStudentSummaryDrivingTestsPage)
